/* ─── The one canonical invocation-type vocabulary (MS-701 AC6, AC6a) ───

   The legend, the chart tooltip, the table badge and the type filter all read
   their label and colour from here. That is the whole point of the module: the
   anti-drift requirement is not "the legend says Pinging Server", it is "nothing
   names a type except by asking this module".

   These labels are product vocabulary and are deliberately NOT translated
   (spec §4.7-22) — they are data-column values, and the server sorts on the same
   English strings, so translating them here would make the rendered order and the
   requested order disagree in every non-English locale.

   `ping` and `initialize` are two stored values with one label (plan D2). The fold
   happens here, at the label layer, and nowhere else: the row keeps its stored
   value and the filter sends both, so the API's data column still renders
   verbatim. */

export interface InvocationTypeEntry {
  /** The entry's own key — also the primary stored value. */
  key: string
  /** The canonical label. Never translated. */
  label: string
  /** CSS custom property carrying this type's colour. */
  colorVar: string
  /**
   * Every stored `invocationType` that renders under this label. Sent verbatim as
   * `invocationTypes` when the entry is used as a filter, which is why *Pinging
   * Server* carries two: clicking it must select both.
   */
  storedValues: string[]
}

export const INVOCATION_TYPES: readonly InvocationTypeEntry[] = [
  { key: 'listing', label: 'Listing Tools', colorVar: 'var(--inv-listing)', storedValues: ['listing'] },
  { key: 'ping', label: 'Pinging Server', colorVar: 'var(--inv-auth)', storedValues: ['ping', 'initialize'] },
  { key: 'search', label: 'Search Call', colorVar: 'var(--inv-search)', storedValues: ['search'] },
  { key: 'invoke', label: 'Invoke Call', colorVar: 'var(--inv-invoke)', storedValues: ['invoke'] },
] as const

/** Every stored value the API can return, derived rather than restated. */
export const STORED_INVOCATION_TYPES: readonly string[] = INVOCATION_TYPES.flatMap(
  t => t.storedValues,
)

const BY_STORED_VALUE = new Map<string, InvocationTypeEntry>(
  INVOCATION_TYPES.flatMap(entry => entry.storedValues.map(v => [v, entry] as const)),
)

/**
 * The entry a stored value renders under, or `undefined` if there is none.
 *
 * Deliberately not a silent fallback onto some default type: a value we do not
 * recognise is a gap between this build and the server, and filing it under the
 * wrong colour would hide that rather than surface it.
 */
export function invocationTypeForStoredValue(stored: string): InvocationTypeEntry | undefined {
  return BY_STORED_VALUE.get(stored)
}

/** The canonical label, or the raw value when we do not recognise it — a strange
 *  badge is better than an empty cell. */
export function invocationTypeLabel(stored: string): string {
  return BY_STORED_VALUE.get(stored)?.label ?? stored
}
